import React, { FC, Suspense, useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import seon from '@seontechnologies/seon-javascript-sdk';

import { useBrandedNavigate } from './hooks/useBrandedNavigate/useBrandedNavigate';
import './scss/index.scss';
import { APP_URLS } from './consts';
import { LayoutInit } from './components/LayoutInit';
import { sessionPreviousLocation } from './utils/storage';
import { Snackbar } from './components/shared/Snackbar';
import { ScrollToTop } from './utils/scroll-to-top';
import ErrorPage from './components/Navigation/ErrorPage/ErrorPage';
import { ProvidersCombiner } from './providers';
import { ContentPage } from './layouts/pages/ContentPage';
import { IframePage } from './layouts/pages/IframePage/IframePage';
import { APP_VERSION } from './version';
import { Login } from './components/Login/Login';
import { CookieBanner } from './components/Compliance/CookieBanner';
import { handleMarketingCookie } from './utils/marketing-preferences-helper';
import { NativeEventBus } from './native/NativeEventBus';
import { ItvMain } from './layouts/pages/ItvMain';
import { PrizesPage } from './layouts/pages/PrizesPage';
import { BingoPage } from './layouts/pages/BingoPage';
import { SpinsPage } from './layouts/pages/SpinsPage';
import { ItvStaticLanding } from './layouts/pages/ItvStaticLanding';
import { ItvGameDetailsPage } from './layouts/pages/ItvGameDetailsPage';
import { ItvBingoDetailsPage } from './layouts/pages/ItvBingoDetailsPage';
import { ItvPromo } from './components/Itv/ItvPromo';
import { MinigamesPage } from './layouts/pages/MinigamesPage/MinigamesPage';
import { SurveyPage } from './layouts/pages/SurveyPage/SurveyPage';
import { FtpPage } from './layouts/pages/FtpPage/FtpPage';
import { SEO } from './components/seo/SEO';
import { useQueryParams } from './hooks/routing';

const AuthenticatedOverlays = React.lazy(() => import('./components/Auth/AuthenticatedOverlays'));

export const App: FC = () => {
  const auth = useAuth();
  const location = useLocation();

  const { navigate, navigateHome } = useBrandedNavigate();
  const isUserAuthenticated = auth?.isAuthenticated;
  const [accountOpen, setAccountOpen] = useState<boolean>(false);
  const storedPreviousLocation = sessionPreviousLocation.get() as { previousLocation: string };
  const [prefLocation, storeLocation] = [location.state?.previousLocation, storedPreviousLocation?.previousLocation];
  const previousLocation = accountOpen ? storeLocation || prefLocation : prefLocation || storeLocation;

  ScrollToTop();

  const handleAccountClose = useCallback(
    (isNestedRoute?: boolean): void => {
      setAccountOpen(false);

      if (isNestedRoute) {
        if (previousLocation) {
          navigate(previousLocation);
        } else {
          navigateHome();
        }
      } else {
        navigate(-1);
      }

      sessionPreviousLocation.remove();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, navigateHome, previousLocation]
  );

  const handleAccountOpen = (): void => {
    // TODO: Refresh wallet totals
    setAccountOpen(true);
  };

  useEffect(() => {
    handleMarketingCookie();

    seon.init();
    window.REACT_APP_VERSION = APP_VERSION || 'dev1';

    if (!NativeEventBus.isNativeBridgeAvailable()) {
      const script = document.createElement('script');

      script.id = 'xtremepush-script';
      script.type = 'text/javascript';
      script.innerHTML = `
        (function(p,u,s,h,e,r,l,i,b) {p['XtremePushObject']=s;p[s]=function(){
          (p[s].q=p[s].q||[]).push(arguments)};i=u.createElement('script');i.async=1;
          i.src=h;b=u.getElementsByTagName('script')[0];b.parentNode.insertBefore(i,b);
        })(window,document,'xtremepush','${process.env.REACT_APP_XP_SDK_URL}');
      `;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const hasAccountQuery = Boolean(location.pathname.includes(APP_URLS.myAccount.home));

    if (isUserAuthenticated) {
      if (hasAccountQuery && !accountOpen) {
        // Open account modal when navigating to account URL
        handleAccountOpen();
      } else if (!hasAccountQuery && accountOpen) {
        // Close account modal when navigating away from account URL
        setAccountOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isUserAuthenticated, accountOpen]);
  const { getQueryParam } = useQueryParams();
  const navigatingToGameInMiniMode = getQueryParam('minigame');

  return (
    <>
      <ProvidersCombiner>
        <SEO canonicalPath={location.pathname} />
        <div id="gameCardZoomPortal" />
        <LayoutInit />
        <Snackbar />
        <Routes location={previousLocation || location}>
          <Route path={APP_URLS.itv.landing} element={<ItvStaticLanding />}></Route>
          {navigatingToGameInMiniMode && <Route path={APP_URLS.itv.gameDetails} element={<ItvGameDetailsPage />} />}

          <Route path={APP_URLS.itv.root} element={<ItvMain />}>
            <Route path={APP_URLS.itv.spinsSubPages} element={<SpinsPage />}></Route>
            <Route path={APP_URLS.itv.prizesSubPages} element={<PrizesPage />}></Route>
            <Route path={APP_URLS.itv.bingoSubPages} element={<BingoPage />}></Route>
            <Route path={APP_URLS.itv.gameDetails} element={<ItvGameDetailsPage />} />
            <Route path={APP_URLS.itv.bingoDetails} element={<ItvBingoDetailsPage />} />

            <Route path={APP_URLS.itv.prizesSubPages} element={<PrizesPage />}></Route>
            <Route path={APP_URLS.itv.bingo} element={<BingoPage />}></Route>
            <Route path={APP_URLS.itv.bingoSubPages} element={<BingoPage />}></Route>
            <Route path={APP_URLS.itv.spins} element={<SpinsPage />}></Route>
            <Route path={APP_URLS.itv.spinsSubPages} element={<SpinsPage />}></Route>
            <Route path={APP_URLS.itv.promotion} element={<ItvPromo />} />
            <Route path={APP_URLS.itv.contentPage} element={<ContentPage />} />
            <Route path={APP_URLS.itv.gam} element={<IframePage />} />
            <Route path={APP_URLS.ftp} element={<FtpPage />} />
          </Route>
          {!isUserAuthenticated && (
            <>
              <Route path={APP_URLS.itv.bingoRegistration} index element={null} />
              <Route path={APP_URLS.itv.spinsRegistration} index element={null} />
            </>
          )}
          <Route path={APP_URLS.contentPage} element={<ContentPage />} />
          <Route path={APP_URLS.gam} element={<IframePage />} />
          <Route path={APP_URLS.minigames} element={<MinigamesPage />} />
          <Route path={APP_URLS.survey} element={<SurveyPage />} />
          {!isUserAuthenticated && <Route path={APP_URLS.login} element={<Login />} />}
          {previousLocation ? null : <Route path="*" element={<ErrorPage />} />}
        </Routes>

        {isUserAuthenticated ? (
          <Suspense fallback={null}>
            <AuthenticatedOverlays
              pathname={location.pathname}
              isMiniMode={navigatingToGameInMiniMode === 'true'}
              accountOpen={accountOpen}
              onAccountClose={handleAccountClose}
            />
          </Suspense>
        ) : (
          location.pathname !== APP_URLS.minigames && navigatingToGameInMiniMode !== 'true' && <CookieBanner />
        )}
      </ProvidersCombiner>
    </>
  );
};
